/**
 * Repositories list — full-width DataTable of every managed repo. One row per
 * repo, enriched with GitHub overview metadata + D1 review health. Clicking a
 * row opens the detail view. Rows pre-sort by last-updated desc; the search
 * box covers findability.
 *
 * ponytail: DataTable has no click-to-sort; if repos grows past a screenful,
 * add a `sort?: (a,b)=>number` column field to DataTable and wire headers.
 */
import { useMemo } from 'react';
import { FolderGit2, Star, AlertCircle } from 'lucide-react';
import { useProjects, type ProjectListItem } from '../lib/data';
import { DataTable, type Column, type FilterConfig } from './DataTable';
import { Skeleton, SkeletonTable } from './Skeleton';
import { Badge } from './ui/badge';

interface ProjectsViewProps {
  onSelectProject: (owner: string, repo: string) => void;
}

const conclusionClass = (c: string | null): string =>
  c === 'approved'
    ? 'bg-success/10 text-success border-success/20'
    : c === 'changes_requested'
    ? 'bg-destructive/10 text-destructive border-destructive/20'
    : 'bg-warning/10 text-warning border-warning/20';
const conclusionLabel = (c: string | null): string => {
  if (c === 'approved') return 'Approved';
  if (c === 'changes_requested') return 'Changes';
  if (c === 'commented') return 'Commented';
  return '—';
};

function timeAgo(iso: string | null): string {
  if (!iso) return '—';
  const ms = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(ms) || ms < 0) return '—';
  const day = Math.floor(ms / 86_400_000);
  if (day >= 30) return `${Math.floor(day / 30)}mo`;
  if (day >= 1) return `${day}d`;
  const hr = Math.floor(ms / 3_600_000);
  if (hr >= 1) return `${hr}h`;
  return `${Math.max(1, Math.floor(ms / 60_000))}m`;
}

export function ProjectsView({ onSelectProject }: ProjectsViewProps) {
  const { items, loading, error } = useProjects();

  // Pre-sort by last-updated desc (most recently active first).
  const rows = useMemo(() => {
    if (!items) return [];
    return [...items].sort((a, b) => {
      const ta = a.overview?.updatedAt ? new Date(a.overview.updatedAt).getTime() : 0;
      const tb = b.overview?.updatedAt ? new Date(b.overview.updatedAt).getTime() : 0;
      return tb - ta;
    });
  }, [items]);

  // Language dropdown filter — only surfaces when ≥2 languages exist (mirrors
  // the PRs page's repo-filter guard), so a single-language org shows no noise.
  const languages = useMemo(() => {
    const set = new Set<string>();
    for (const p of items ?? []) if (p.overview?.language) set.add(p.overview.language);
    return [...set].sort();
  }, [items]);
  const repoFilters: FilterConfig<ProjectListItem>[] = useMemo(
    () =>
      languages.length > 1
        ? [{ key: 'language', label: 'Language', options: languages.map((l) => ({ value: l, label: l })), predicate: (p, v) => (p.overview?.language ?? '') === v }]
        : [],
    [languages],
  );

  const columns: Column<ProjectListItem>[] = [
    {
      key: 'repo',
      header: 'Repository',
      render: (p) => (
        <div className="flex items-center gap-2.5">
          {p.overview?.ownerAvatar ? (
            <img src={p.overview.ownerAvatar} alt="" width={22} height={22} style={{ borderRadius: 4 }} />
          ) : (
            <FolderGit2 size={16} className="text-muted-foreground" />
          )}
          <div className="min-w-0">
            <div className="font-semibold text-foreground">{p.owner}/{p.repo}</div>
            {p.overview?.description ? (
              <div className="text-xs truncate text-muted-foreground max-w-[360px]">
                {p.overview.description}
              </div>
            ) : null}
          </div>
        </div>
      ),
    },
    {
      key: 'branch',
      header: 'Branch',
      render: (p) => (
        <span className="font-mono text-xs text-muted-foreground">
          {p.overview?.defaultBranch ?? '—'}
          {p.overview?.language ? <span className="ml-1.5 opacity-60">· {p.overview.language}</span> : null}
        </span>
      ),
    },
    {
      key: 'issues',
      header: 'Issues',
      align: 'right',
      render: (p) => p.overview ? (
        <span title="GitHub open_issues_count includes open PRs" className="font-mono text-foreground">
          <Star size={11} className="inline mr-1 opacity-50 align-text-bottom" />
          {p.overview.stars}
          <span className="opacity-40 mx-1.5">·</span>
          {p.overview.openIssues}
        </span>
      ) : '—',
    },
    {
      key: 'reviews',
      header: 'Reviews',
      align: 'right',
      render: (p) => <span className="font-mono text-foreground">{p.stats.reviewCount}</span>,
    },
    {
      key: 'score',
      header: 'Avg Score',
      align: 'right',
      render: (p) => (
        <span className="font-mono font-semibold text-foreground">
          {p.stats.reviewCount > 0 ? p.stats.avgScore : '—'}
        </span>
      ),
    },
    {
      key: 'last',
      header: 'Last Review',
      render: (p) => p.stats.lastConclusion ? (
        <Badge className={conclusionClass(p.stats.lastConclusion)}>
          {conclusionLabel(p.stats.lastConclusion)}
          {p.stats.lastScore != null ? ` · ${p.stats.lastScore}` : ''}
        </Badge>
      ) : <span className="text-muted-foreground">—</span>,
    },
    {
      key: 'updated',
      header: 'Updated',
      align: 'right',
      render: (p) => (
        <span className="font-mono text-xs text-muted-foreground">
          {timeAgo(p.overview?.updatedAt ?? null)}
        </span>
      ),
    },
  ];

  if (loading) {
    return (
      <div className="flex flex-col gap-3" aria-busy="true" aria-label="Loading repositories">
        <Skeleton width={160} height={11} rounded={3} />
        <SkeletonTable rows={6} cols={['30%', '12%', '12%', '10%', '10%', '14%', '12%']} />
      </div>
    );
  }
  if (error) {
    return (
      <div className="text-destructive flex gap-2 items-center">
        <AlertCircle size={16} /> {error}
      </div>
    );
  }
  if (!items || items.length === 0) {
    return (
      <div className="text-center p-12 border border-dashed border-border rounded-xl text-muted-foreground">
        <FolderGit2 size={28} className="opacity-40 mb-2 mx-auto" />
        <div className="font-semibold text-foreground">No repositories yet</div>
        <div className="text-sm mt-1">
          Repositories appear once a webhook fires or an admin adds a repo under Settings.
        </div>
      </div>
    );
  }

  return (
    <DataTable
      rows={rows}
      columns={columns}
      rowKey={(p) => `${p.owner}/${p.repo}`}
      onRowClick={(p) => onSelectProject(p.owner, p.repo)}
      searchKeys={[(p) => `${p.owner}/${p.repo}`, (p) => p.overview?.description ?? '', (p) => p.overview?.language ?? '']}
      searchPlaceholder="Search repositories…"
      filters={repoFilters}
      pageSize={10}
    />
  );
}
